import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";

import { auth } from "../../../firebase";
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  reload,
} from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getFirestore, doc, setDoc } from "firebase/firestore";

import { setUser } from "../../../redux/actions";
import AuthHandler from "../../../authHandlers/AuthHandler";
import { assignUserRole } from "../../utils/roleManagement";
import LoadingCircleSmall from "../../utils/LoadingCircleSmall";
import "./HeroRegisterUser.css";

const HeroRegisterUser = () => {
  // -------------------- STATE -------------------- //
  const [company, setCompany] = useState("");
  const [collectionBrand, setCollectionBrand] = useState("");
  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");

  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [showAdditionalFields, setShowAdditionalFields] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [currentUser, setCurrentUser] = useState(null);
  const [processing, setProcessing] = useState(false);

  const [displayedText, setDisplayedText] = useState("");
  const [cursorVisible, setCursorVisible] = useState(true);
  const typingPhrase = "<Welcome to Hero.../>";

  // Hooks
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Cloud Functions references
  const functions = getFunctions();
  const firestoreOps = httpsCallable(functions, "firestoreOperations");
  const assignUserRoleCF = assignUserRole; // CF wrapper
  const db = getFirestore();

  // Use a ref to ensure invitation verification runs only once.
  const verifiedRef = useRef(false);

  // -------------------- INVITATION VERIFICATION -------------------- //
  useEffect(() => {
    if (verifiedRef.current) return;

    const queryParams = new URLSearchParams(location.search);
    const companyParam = queryParams.get("company");
    const collectionBrandParam = queryParams.get("collectionBrand");
    const emailParam = queryParams.get("email");
    const tokenParam = queryParams.get("token");

    console.log("Parsed invitation parameters:", {
      companyParam,
      collectionBrandParam,
      emailParam,
      tokenParam,
    });

    // Validate that all required parameters are present
    if (!companyParam || !collectionBrandParam || !emailParam || !tokenParam) {
      console.log("Missing required invitation parameters.");
      alert("Invalid or incomplete invitation link.");
      navigate("/error");
      return;
    }

    // Update state with URL parameters
    setCompany(companyParam);
    setCollectionBrand(collectionBrandParam);
    setEmail(emailParam);
    setToken(tokenParam);

    const verifyInvitation = async () => {
      try {
        // Initialize the new cloud function for unauthenticated reads from Users.
        const firestoreReadDocForUsers = httpsCallable(
          functions,
          "firestoreReadDocForUsers"
        );

        // Call the new function:
        const result = await firestoreReadDocForUsers({
          company: companyParam,
          collectionBrand: collectionBrandParam,
          systemName: "Users",
          uniqueId: emailParam,
        });

        // Check that the document was found and contains data.
        if (!result.data || !result.data.found || !result.data.data) {
          console.log("No user document found for:", emailParam);
          alert("Invalid or expired invitation link (user not found).");
          navigate("/error");
          return;
        }
        const userData = result.data.data;
        console.log("User Data:", userData);
        if (userData.email !== emailParam) {
          console.log(
            "User email mismatch:",
            "user email =",
            userData.email,
            "URL email =",
            emailParam
          );
          alert(
            "Invitation mismatch: This link was not intended for this email."
          );
          navigate("/error");
          return;
        }
        if (userData.state !== "Invited") {
          console.log(
            "User state invalid:",
            "current state =",
            userData.state,
            "expected state = Invited"
          );
          alert("This invitation link has already been used or is invalid.");
          navigate("/error");
          return;
        }
        console.log("Invitation verified successfully. User is Invited.");
        verifiedRef.current = true;
      } catch (err) {
        console.error("Error verifying user document:", err);
        alert("Unable to verify your invitation. Please contact support.");
        navigate("/error");
      }
    };

    verifyInvitation();
  }, [location.search, navigate, functions]);

  // -------------------- TYPING EFFECT -------------------- //
  useEffect(() => {
    let typeTimeout;
    let cursorTimeout;
    setDisplayedText("");
    const typeWord = (index) => {
      if (index < typingPhrase.length) {
        setDisplayedText((prev) => prev + typingPhrase[index]);
        typeTimeout = setTimeout(() => typeWord(index + 1), 150);
      } else {
        typeTimeout = setTimeout(() => {
          setDisplayedText("");
          typeWord(0);
        }, 3000);
      }
    };
    typeWord(0);
    const blinkCursor = () => {
      setCursorVisible((prev) => !prev);
      cursorTimeout = setTimeout(blinkCursor, 500);
    };
    blinkCursor();
    return () => {
      clearTimeout(typeTimeout);
      clearTimeout(cursorTimeout);
    };
  }, []);

  // -------------------- REGISTRATION FLOW -------------------- //
  const handleContinue = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!showAdditionalFields) {
      setShowAdditionalFields(true);
      return;
    }

    setProcessing(true);
    console.log("Registration process started. Processing state set to true.");

    try {
      // Create the new user in Firebase Auth.
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      const firebaseUser = userCredential.user;
      setCurrentUser(firebaseUser);
      setSuccess(`Welcome, ${name}! Please proceed with email verification.`);
      console.log("Firebase user created:", firebaseUser.email);

      // Dispatch user info to Redux store.
      dispatch(
        setUser({
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          name,
        })
      );

      // Assign the custom claim in the format "Hero-{company}-{collectionBrand}".
      const customClaim = `Hero-${company}-${collectionBrand}`;
      await assignUserRoleCF(firebaseUser.uid, customClaim);
      console.log("Custom claim assigned:", customClaim);

      // Update the Users document (located at /{company}/{collectionBrand}/Users/{email})
      // to set state to "Active". This marks the registration as complete.
      await firestoreOps({
        operation: "update",
        company,
        collectionBrand,
        systemName: "Users",
        uniqueId: email,
        documentData: { state: "Active" },
        auth: true,
      });
      console.log("User document updated to Active state.");

      // Send email verification.
      await sendEmailVerification(firebaseUser);
      console.log("Email verification sent.");

      // After successful registration and before navigation, add:
      const normalizedEmail = email.replace(/\./g, "").replace(/@/g, "");
      await setDoc(
        doc(db, "TPC_Group", "TechPerformanceCentre", "profile-images", normalizedEmail),
        { url: null }
      );
      console.log("Profile image document created/updated.");

    } catch (err) {
      console.error("Error in handleContinue:", err);
      setError(err.message || "An error occurred. Please try again later.");
    } finally {
      setProcessing(false);
      console.log(
        "Registration process completed. Processing state set to false."
      );
    }
  };

  const handleVerificationCheck = async () => {
    try {
      const userToCheck = currentUser || auth.currentUser;
      if (!userToCheck) {
        alert("No current user found. Please try again.");
        return;
      }
      await reload(userToCheck);
      if (userToCheck.emailVerified) {
        navigate("/hero-dashboard");
      } else {
        alert("Your email is not verified. Please check your inbox.");
      }
    } catch (error) {
      setError("An error occurred while checking verification status.");
    }
  };

  const handleResendVerification = async () => {
    try {
      const userToSend = currentUser || auth.currentUser;
      if (!userToSend) {
        alert("No current user found. Please try again.");
        return;
      }
      await sendEmailVerification(userToSend);
      alert("Verification email resent. Please check your inbox.");
    } catch (error) {
      setError("Failed to resend verification email. Please try again later.");
    }
  };

  // -------------------- UI RENDERING -------------------- //
  return (
    <AuthHandler>
      {({ isThirdStep }) => (
        <div className="hero-register-container">
          <div className="hero-register-left">
            <div className="register-word-container">
              <div className="register-word">
                {displayedText}
                <span
                  className={`register-cursor ${
                    cursorVisible ? "visible" : "hidden"
                  }`}
                >
                  |
                </span>
              </div>
            </div>
          </div>
          <div className="hero-register-right">
            {processing ? (
              <div className="processing-overlay">
                <LoadingCircleSmall />
              </div>
            ) : (
              <>
                {!isThirdStep ? (
                  <>
                    <h2 className="register-title">
                      Company: {company} ({collectionBrand})
                    </h2>
                    <form className="register-form" onSubmit={handleContinue}>
                      <h2 className="register-title">
                        {showAdditionalFields
                          ? "Complete Your Registration"
                          : "Register with Email"}
                      </h2>
                      {error && (
                        <p className="error-message">
                          {error} (see console for details)
                        </p>
                      )}
                      {success && <p className="success-message">{success}</p>}
                      {!showAdditionalFields ? (
                        <>
                          <input
                            type="email"
                            placeholder="Email"
                            className="register-input"
                            value={email}
                            disabled
                          />
                          <div className="register-checkbox-container">
                            <input
                              type="checkbox"
                              className="register-checkbox"
                              required
                            />
                            <label className="register-checkbox-label">
                              I agree to the{" "}
                              <Link to="/terms-conditions">
                                Terms and Conditions
                              </Link>{" "}
                              and <Link to="/privacy">Privacy Policies</Link>.
                            </label>
                          </div>
                        </>
                      ) : (
                        <>
                          <input
                            type="text"
                            placeholder="Name & Surname"
                            className="register-input"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                          />
                          <input
                            type="password"
                            placeholder="Password"
                            className="register-input"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                          />
                          <p className="register-terms">
                            By submitting this form, you agree to the Terms of
                            Use and consent to receiving communications from us.
                          </p>
                        </>
                      )}
                      <button type="submit" className="register-button">
                        {showAdditionalFields ? "Submit" : "Continue"}
                      </button>
                    </form>
                  </>
                ) : (
                  <>
                    <div className="verification-message">
                      <h2 className="register-title">Almost There!</h2>
                      <p className="register-terms">
                        We have sent an email verification link to your inbox.
                        Please follow the link to finalize your account.
                      </p>
                      <h3 className="register-title">
                        If your browser did not redirect after verifying your
                        email, click below:
                      </h3>
                      <button
                        onClick={handleVerificationCheck}
                        className="refresh-button"
                      >
                        I’ve Verified My Email
                      </button>
                      <p className="register-terms">
                        Didn’t receive the email?{" "}
                        <button
                          onClick={handleResendVerification}
                          className="resend-button"
                        >
                          Resend
                        </button>
                      </p>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </AuthHandler>
  );
};

export default HeroRegisterUser;
